import { useEffect, useState } from 'react';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { FieldArrayMethodProps, useFieldArray, useForm } from 'react-hook-form';

import { Column, ListingForm, RHFAndCustomSettings } from './types';

export function useListing<
  Data extends Record<string, any>,
  Context extends any = any,
  ColumnType extends string = string,
  CustomColumnType extends string = string,
>(
  initialData: ListingForm<Data, Context, ColumnType, CustomColumnType>,
  props?: RHFAndCustomSettings<Data, Context, ColumnType, CustomColumnType>
) {
  type ColumnMeta = NonNullable<Column<Data, Context, ColumnType, CustomColumnType>['columnMeta']>;

  const { handleOpen: onOpenConfirmationModal } = useModal(ModalsKeys.Confirmation);

  const form = useForm<ListingForm<Data, Context, ColumnType, CustomColumnType>>({
    defaultValues: initialData,
    shouldUnregister: true,
    ...(props ?? {}),
  });

  const [columns, setColumns] = useState<Column<Data, Context, ColumnType, CustomColumnType>[]>(
    initialData.headerColumns
  );

  const {
    fields: tableData,
    append: onAddRow,
    remove: onRemove,
    insert: onInsertRow,
    update: onUpdate,
    move: onMoveRow,
  } = useFieldArray({
    control: form.control,
    name: 'bodyFields' as any,
  });

  const notifySubscriber = () => {
    props?.onSubscribe?.(form.getValues('bodyFields'));
  };

  const handleAddColumn = (column: Column<Data, Context, ColumnType, CustomColumnType>, index?: number) => {
    setColumns(prev => {
      if (index !== undefined && index >= 0 && index <= prev.length) {
        return [...prev.slice(0, index), column, ...prev.slice(index)];
      }

      return [...prev, column];
    });

    notifySubscriber();
  };

  const handleRemoveColumn = (columnIndex: number) => {
    setColumns(prev => prev.filter((_, index) => index !== columnIndex));
    notifySubscriber();
  };

  const handleUpdateColumnType = (columnIndex: number, nextValue: ColumnType) => {
    setColumns(prev =>
      prev.map((column, index) => {
        if (index === columnIndex) return { ...column, columnType: nextValue, renderKey: nextValue };
        return column;
      })
    );

    notifySubscriber();
  };

  const handleUpdateColumnMeta = <Key extends keyof ColumnMeta>(
    columnIndex: number,
    key: Key,
    value: ColumnMeta[Key]
  ) => {
    setColumns(prev =>
      prev.map((column, index) => {
        if (index === columnIndex) return { ...column, columnMeta: { ...(column.columnMeta ?? {}), [key]: value } };
        return column;
      })
    );

    notifySubscriber();
  };

  const handleResetColumns = () => {
    onOpenConfirmationModal({
      title: 'Confirmation',
      message: 'Are you sure you want to reset all data?',
      onConfirm: () => {
        setColumns(initialData.headerColumns);
        notifySubscriber();
      },
    });
  };

  const handleAddRow = (row: Data, options?: FieldArrayMethodProps) => {
    onAddRow(row, options);

    notifySubscriber();
  };

  const handleInsertRow = (row: Data, index: number, options?: FieldArrayMethodProps) => {
    onInsertRow(index, row, options);

    notifySubscriber();
  };

  const handleUpdateRow = (row: Data, index: number) => {
    onUpdate(index, row);

    notifySubscriber();
  };

  const handleMoveRow = (fromIndex: number, toIndex: number) => {
    onMoveRow(fromIndex, toIndex);

    notifySubscriber();
  };

  const handleRemoveRow = (index: number) => {
    onOpenConfirmationModal({
      title: 'Confirmation',
      message: 'Are you sure you want to remove this row?',
      onConfirm: () => {
        onRemove(index);
        notifySubscriber();
      },
    });
  };

  const unregisterBodyFieldsByColumnId = (id: string, column: string) => {
    const currentRows = form.getValues('bodyFields');
    const updatedRows = currentRows.map(row => {
      if (!row[column]) return row;

      const { [id]: _, ...restRows } = row[column];
      return { ...row, [column]: restRows };
    });

    form.setValue('bodyFields', updatedRows);
  };

  const handleCopyLastRow = () => {
    const rows = form.getValues('bodyFields');
    handleAddRow(rows[rows.length - 1]);
  };

  useEffect(() => {
    const subscribeMode = props?.subscribeMode ?? ['change'];

    const subscription = form.watch(({ bodyFields }, { type }) => {
      if (!type || !subscribeMode.includes(type)) return;

      if (!bodyFields || typeof props?.onSubscribe !== 'function') return;

      const cleanBodyFields = bodyFields.filter((row): row is Data => row !== undefined);

      props?.onSubscribe(cleanBodyFields);
    });

    return subscription.unsubscribe;
  }, [form, props, props?.onSubscribe]);

  return {
    form,
    columns,
    onRemove,
    handleAddRow,
    handleMoveRow,
    handleAddColumn,
    handleRemoveRow,
    handleInsertRow,
    handleUpdateRow,
    handleCopyLastRow,
    handleRemoveColumn,
    handleResetColumns,
    handleUpdateColumnType,
    handleUpdateColumnMeta,
    unregisterBodyFieldsByColumnId,
    tableData: tableData as Data[],
  };
}
